// AvailabilityInfo – Standalone page showing a single availability's details.
// Accessed via direct URL route (/book/availability/:providerId/:zdt).
// Supports viewing, updating, and deleting an individual availability.
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  getAvailabilityInfoByStart,
  updateAvailability,
  deleteAvailability,
  getAvailabilityById,
  getSlotsByDate,
} from "../APIs/availability";

// Default to the user's system timezone
const defaultZone = (() => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
    );
  } catch {
    return "America/New_York";
  }
})();

// --- formatting helpers ---
function fmtDate(dateStrOrZdt) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStrOrZdt)) {
    const [y, m, d] = dateStrOrZdt.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    return dt.toLocaleDateString([], {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
  const iso = String(dateStrOrZdt).split("[")[0];
  const dt = new Date(iso);
  return dt.toLocaleDateString([], {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function fmtTime(zdtString) {
  const isoWithOffset = String(zdtString).split("[")[0];
  const d = new Date(isoWithOffset);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// --- edit helpers (build ZonedDateTime strings with correct offset) ---
const pad2 = (n) => String(n).padStart(2, "0");

function getZoneOffsetString(dateStr, timeStr, zoneId) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  const approxUtc = new Date(Date.UTC(y, m - 1, d, hh, mm, 0, 0));
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: zoneId,
    hour12: false,
    timeZoneName: "shortOffset",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const parts = fmt.formatToParts(approxUtc);
  const tzPart = parts.find((p) => p.type === "timeZoneName")?.value || "GMT+0";
  const m1 = tzPart.match(/([+-])(\d{1,2})(?::?(\d{2}))?$/);
  if (!m1) return "+00:00";
  const sign = m1[1] === "-" ? "-" : "+";
  const H = pad2(Number(m1[2]));
  const M = pad2(Number(m1[3] || "0"));
  return `${sign}${H}:${M}`;
}

function toZonedDateTimeString(dateStr, timeStr, zoneId) {
  const offset = getZoneOffsetString(dateStr, timeStr, zoneId);
  return `${dateStr}T${timeStr}:00${offset}[${zoneId}]`;
}

function extractZoneFromZdt(zdtString) {
  const m = String(zdtString).match(/\[([^\]]+)\]$/);
  return m ? m[1] : defaultZone;
}

function toInputsFromZdt(zdtString) {
  // Returns { date: 'YYYY-MM-DD', time: 'HH:mm' }
  const iso = String(zdtString).split("[")[0]; // keep offset
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mi = pad2(d.getMinutes());
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
}

function sameInstant(a, b) {
  const ta = new Date(String(a).split("[")[0]).getTime();
  const tb = new Date(String(b).split("[")[0]).getTime();
  return ta === tb;
}

export default function AvailabilityInfo() {
  const { providerId, availabilityId, zdt } = useParams(); // Now supports both route patterns
  const startParam = useMemo(() => (zdt ? decodeURIComponent(zdt) : ""), [zdt]);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState(null);

  // edit UI state
  const [editing, setEditing] = useState(false);
  const [updDate, setUpdDate] = useState("");
  const [updStart, setUpdStart] = useState("");
  const [updEnd, setUpdEnd] = useState("");
  const [updZone, setUpdZone] = useState(defaultZone);
  const [updTele, setUpdTele] = useState(undefined); // boolean | undefined
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [msg, setMsg] = useState("");

  //Display slot end and start time
  const [slotEndOverride, setSlotEndOverride] = useState(null);
  const [slotStartOverride, setSlotStartOverride] = useState(null);

  const fetchInfo = async () => {
    setLoading(true);
    setErr("");
    try {
      let data;

      // If we have availabilityId, use that (more reliable)
      if (availabilityId) {
        data = await getAvailabilityById(providerId, availabilityId, {
          zone: defaultZone,
        });
      }
      // Otherwise fall back to start time lookup
      else if (startParam) {
        data = await getAvailabilityInfoByStart(providerId, {
          start: startParam,
          zone: defaultZone,
        });
      } else {
        throw new Error("No availability ID or start time provided");
      }

      setInfo(data);
      // Resolve slot end time (slot-level) when we have a slot start
      try {
        const slotStartZdt = startParam || data.startDateTime; // startParam is the clicked slot start
        if (slotStartZdt) {
          const { date: slotDate } = toInputsFromZdt(slotStartZdt);

          const slots = await getSlotsByDate(Number(providerId), {
            date: slotDate,
            zone: defaultZone,
          });

          const match = (slots || []).find((s) =>
            sameInstant(s.slotStart, slotStartZdt),
          );

          setSlotStartOverride(match?.slotStart || null);
          setSlotEndOverride(match?.slotEnd || null);
        } else {
          setSlotStartOverride(null);
          setSlotEndOverride(null);
        }
      } catch {
        setSlotStartOverride(null);
        setSlotEndOverride(null);
      }

      // prefill edit fields from response
      const z = extractZoneFromZdt(data.startDateTime) || defaultZone;
      const { date, time } = toInputsFromZdt(data.startDateTime);
      const { time: endTime } = toInputsFromZdt(data.endDateTime);
      setUpdZone(z);
      setUpdDate(date);
      setUpdStart(time);
      setUpdEnd(endTime);
      setUpdTele(Boolean(data.telehealth));
    } catch (e) {
      setErr(e?.message || "Failed to load availability info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (providerId && (availabilityId || startParam)) fetchInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, availabilityId, startParam]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!info) return;
    setMsg("");
    // minimal client-side checks
    if (!updDate || !updStart || !updEnd) {
      setMsg("Please provide date, start, and end.");
      return;
    }
    if (updStart >= updEnd) {
      setMsg("Start must be before End.");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        startDateTime: toZonedDateTimeString(updDate, updStart, updZone),
        endDateTime: toZonedDateTimeString(updDate, updEnd, updZone),
        telehealth: updTele,
      };
      await updateAvailability(
        Number(providerId),
        Number(info.availabilityId),
        payload,
      );

      // After successful update, use ID-based fetch to refresh
      const fresh = await getAvailabilityById(providerId, info.availabilityId, {
        zone: defaultZone,
      });
      setInfo(fresh);
      setEditing(false);
      setMsg("Availability updated.");

      // Update URL if we're currently using start time in the route
      if (zdt && !availabilityId) {
        const newStartEncoded = encodeURIComponent(fresh.startDateTime);
        navigate(`/book/availability/${providerId}/${newStartEncoded}`, {
          replace: true,
        });
      }
    } catch (e) {
      setMsg(e?.message || "Failed to update availability");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!info) return;
    const ok = window.confirm(
      "Delete this availability? This action cannot be undone.",
    );
    if (!ok) return;
    try {
      setDeleting(true);
      await deleteAvailability(Number(providerId), Number(info.availabilityId));
      // Navigate back to the list/view page or back one step
      navigate(-1);
    } catch (e) {
      setMsg(e?.message || "Failed to delete availability");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-3xl p-4 space-y-4">
      <button
        onClick={() => window.history.back()}
        className="mb-4 text-sm px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300 flex items-center gap-1"
      >
        ← Back
      </button>

      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">Availability Details</h3>
        <div className="text-xs text-gray-500">{defaultZone}</div>
      </div>

      <div className="flex gap-2">
        <Link
          to={`/provider/${encodeURIComponent(providerId)}/availabilities`}
          className="text-sm px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300"
        >
          ← Back to View Availabilities
        </Link>
        <button
          onClick={fetchInfo}
          className="text-sm px-3 py-1.5 rounded bg-gray-800 text-white hover:bg-black"
        >
          Refresh
        </button>
      </div>

      {loading && <p className="text-gray-600">Loading…</p>}
      {err && !loading && (
        <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200">
          {err}
        </div>
      )}

      {info && !loading && !err && (
        <>
          <div className="p-4 border rounded bg-white space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-xs uppercase text-gray-500">Date</div>
                <div className="text-base font-medium">
                  {fmtDate(info.date)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Type</div>
                <div className="text-base font-medium">{info.type}</div>
              </div>

              <div>
                <div className="text-xs uppercase text-gray-500">
                  Start time
                </div>
                <div className="text-base font-medium">
                  {fmtTime(
                    slotStartOverride || startParam || info.startDateTime,
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">End time</div>
                <div className="text-base font-medium">
                  {fmtTime(slotEndOverride || info.endDateTime)}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase text-gray-500">Duration</div>
                <div className="text-base font-medium">
                  {info.durationMinutes} minutes
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">
                  Telehealth
                </div>
                <div className="text-base font-medium">
                  {info.telehealth ? "Yes" : "No"}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase text-gray-500">Status</div>
                <span
                  className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                    info.status === "FREE"
                      ? "bg-green-100 text-green-700"
                      : info.status === "PAST"
                        ? "bg-gray-100 text-gray-700"
                        : info.status === "BOOKED"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {info.status}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => setEditing((v) => !v)}
              className="px-4 py-2 rounded bg-yellow-500 text-white hover:bg-yellow-600"
            >
              {editing ? "Cancel Edit" : "Update"}
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className={`px-4 py-2 rounded text-white ${deleting ? "bg-red-300" : "bg-red-600 hover:bg-red-700"}`}
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>

          {msg && <div className="text-sm text-gray-700">{msg}</div>}

          {/* Inline edit panel */}
          {editing && (
            <form
              onSubmit={handleUpdate}
              className="p-4 border rounded bg-white space-y-3"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 text-sm font-medium">Date</label>
                  <input
                    type="date"
                    value={updDate}
                    onChange={(e) => setUpdDate(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    Time Zone
                  </label>
                  <input
                    type="text"
                    value={updZone}
                    onChange={(e) => setUpdZone(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    placeholder="e.g. America/New_York"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    Start time
                  </label>
                  <input
                    type="time"
                    step={900}
                    value={updStart}
                    onChange={(e) => setUpdStart(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    End time
                  </label>
                  <input
                    type="time"
                    step={900}
                    value={updEnd}
                    onChange={(e) => setUpdEnd(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    Telehealth
                  </label>
                  <select
                    value={
                      updTele === undefined ? "" : updTele ? "true" : "false"
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setUpdTele(v === "" ? undefined : v === "true");
                    }}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="">(no change)</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className={`px-4 py-2 rounded text-white ${saving ? "bg-blue-300" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}
